import { useEffect, useState } from 'react';
import { FieldConfig } from '../../../../../components/CustomFormsComponents/CustomField';
import { ValidationRule } from '../../../../../components/CustomFormsComponents/hooks/useValidation';
import { useAppSelector } from '../../../../../hooks/reduxHooks';
import { TEST_IDS } from '../testIds';
import { HABILITATION_TITLE_VALID_OPTIONS, isExisitingHabilitationTitle } from '../../../../../commons/presentations';
export const RESPONSIBLE_CHARGE_ID_VALUE = 1;


const useResponsibleFieldsConfig = ({ t, formData, handleBlurValidation }: any) => {
    const [responsibleFieldsConfig, setResponsibleFieldsConfig] = useState<FieldConfig[]>([]);

    const { cargos, tiposContratacion, representaciones } = useAppSelector(
        (state) => state.datosParametricos.data
    );
    
    useEffect(() => {
        // La SRT exige título, matrícula, entidad y horas a Higiene y Seguridad y a
        // Medicina del Trabajo (§3.3.3), no al Responsable de los Datos: para ese
        // cargo quedan opcionales.
        const exigeHabilitacion = Number(formData.charge) !== RESPONSIBLE_CHARGE_ID_VALUE;
        const requerido: ValidationRule[] = exigeHabilitacion ? [{ name: 'required' }] : [];
        const fieldsConfig: FieldConfig[] = [
            {
                type: 'text',
                label: t('responsiblesAndContractorsForms.newResponsible.inputs.name') as string,
                name: 'name',
                required: true,
                value: formData.fullname,
                disabled: false,
                helperText: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.name'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 6 },
                testID: TEST_IDS.responsibles.newResponsibleFormFields.name,
                validations: [{ name: 'required' }, { name: 'lettersOnly' }]
            },
            {
                type: 'text',
                label: t('responsiblesAndContractorsForms.newResponsible.inputs.surname') as string,
                name: 'surname',
                required: true,
                value: formData.fullname,
                disabled: false,
                helperText: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.surname'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 6 },
                testID: TEST_IDS.responsibles.newResponsibleFormFields.surname,
                validations: [{ name: 'required' }, { name: 'lettersOnly' }]
            },
            {
                type: 'text',
                label: t('responsiblesAndContractorsForms.newResponsible.inputs.cuit') as string,
                name: 'cuit',
                required: true,
                value: formData.cuit,
                disabled: false,
                helperText: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.cuit'
                ) as string,
                onBlurValidation: handleBlurValidation,
                inputType: 'number',
                maxLength: 11,
                minLength: 11,
                gridSpace: { xs: 12, sm: 6 },
                testID: TEST_IDS.responsibles.newResponsibleFormFields.cuit,
                validations: [
                    { name: 'required' },
                    { name: 'fixedLength', param: 11 },
                    { name: 'cuitModulo11' }
                ]
            },
            {
                type: 'autocomplete',
                label: t('responsiblesAndContractorsForms.newResponsible.inputs.charge') as string,
                name: 'charge',
                required: true,
                value: formData.charge,
                disabled: !cargos,
                options: cargos || [],
                helperText: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.charge'
                ) as string,
                onBlurValidation: handleBlurValidation,
                // Ancho completo: 'Responsable de los Datos del Formulario' no entra
                // en media columna y quedaba truncado.
                gridSpace: { xs: 12 },
                testID: TEST_IDS.responsibles.newResponsibleFormFields.charge,
                validations: [{ name: 'required' }]
            },
            {
                type: 'autocomplete',
                label: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.respresentation'
                ) as string,
                name: 'respresentation',
                required: true,
                value: formData.representation,
                options: representaciones || [],
                disabled: !representaciones,
                helperText: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.respresentation'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 6 },
                testID: TEST_IDS.responsibles.newResponsibleFormFields.respresentation,
                validations: [{ name: 'required' }]
            },
            {
                type: 'autocomplete',
                label: t('responsiblesAndContractorsForms.newResponsible.inputs.status') as string,
                name: 'status',
                required: true,
                value: formData.status,
                disabled: !tiposContratacion,
                helperText: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.status'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 6 },
                testID: TEST_IDS.responsibles.newResponsibleFormFields.status,
                options: tiposContratacion || [],
                validations: [{ name: 'required' }]
            },
            {
                type: 'autocomplete-default',
                label: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.habilitationTitle'
                ) as string,
                name: 'habilitationTitle',
                required: exigeHabilitacion,
                value: formData.habilitationTitle,
                disabled: false,
                helperText: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.habilitationTitle'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12 },
                options: HABILITATION_TITLE_VALID_OPTIONS,
                getOptionLabel: (option: string) => option,
                testID: TEST_IDS.responsibles.newResponsibleFormFields.habilitationTitle,
                validations: [...requerido]
            },
            ...(!isExisitingHabilitationTitle(formData.habilitationTitle) && formData.habilitationTitle !== ''
                ? [
                      {
                          type: 'textarea' as const,
                          label: t(
                              'responsiblesAndContractorsForms.newResponsible.inputs.otherHabilitationTitle'
                          ) as string,
                          name: 'otherHabilitationTitle',
                          required: true,
                          value: isExisitingHabilitationTitle(formData.habilitationTitle)
                              ? formData?.habilitationTitle
                              : formData.otherHabilitationTitle,
                          disabled: false,
                          helperText: t(
                              'responsiblesAndContractorsForms.newResponsible.inputs.otherHabilitationTitle'
                          ) as string,
                          onBlurValidation: handleBlurValidation,
                          gridSpace: { xs: 12 },
                          maxLength: 49,
                          testID: TEST_IDS.responsibles.newResponsibleFormFields
                              .otherHabilitationTitle,
                          validations: [{ name: 'required' }, {name: 'maxLength', param: 49}]
                      } as FieldConfig
                  ]
                : []),
            {
                type: 'text',
                label: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.licenceNumber'
                ) as string,
                name: 'licenceNumber',
                required: exigeHabilitacion,
                value: formData.licenceNumber,
                disabled: false,
                helperText: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.licenceNumber'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 6 },
                maxLength: 39,
                testID: TEST_IDS.responsibles.newResponsibleFormFields.licenceNumber,
                validations: [...requerido, { name: 'maxLength', param: 39 }]
            },
            {
                type: 'text',
                label: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.grantingEntity'
                ) as string,
                name: 'grantingEntity',
                required: exigeHabilitacion,
                value: formData.grantingEntity,
                disabled: false,
                helperText: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.grantingEntity'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 6 },
                maxLength: 39,
                testID: TEST_IDS.responsibles.newResponsibleFormFields.grantingEntity,
                validations: [...requerido, { name: 'maxLength', param: 39 }]
            },
            {
                type: 'text',
                label: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.hoursQuantity'
                ) as string,
                name: 'hoursQuantity',
                required: exigeHabilitacion,
                value: formData.hoursQuantity,
                disabled: false,
                inputType: 'number',
                helperText: t(
                    'responsiblesAndContractorsForms.newResponsible.inputs.hoursQuantity'
                ) as string,
                onBlurValidation: handleBlurValidation,
                gridSpace: { xs: 12, sm: 6 },
                maxLength: 3,
                testID: TEST_IDS.responsibles.newResponsibleFormFields.hoursQuantity,
                validations: exigeHabilitacion
                    ? [
                          { name: 'required' },
                          { name: 'minValue', param: 1 },
                          { name: 'maxValue', param: 744 }
                      ]
                    : [{ name: 'maxValue', param: 744 }]
            }
        ];

        setResponsibleFieldsConfig(fieldsConfig);
    }, [t, formData.charge, formData.habilitationTitle]);

    return {
        responsibleFieldsConfig
    };
};

export default useResponsibleFieldsConfig;
